import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { api, formatPrice } from "../../lib/api";
import { useSeo } from "../../lib/useSeo";
import { PlayStoreButton } from "../../components/PlayStoreCTA";

const KIND_BY_VARIANT = {
  city: "huurwoningen",
  appartement: "appartement-huren",
  studio: "studio-huren",
  kamer: "kamer-huren",
  huis: "huis-huren",
};

const Card = ({ d }) => (
  <Link
    to={`/huurwoning/${d.slug}`}
    className="block overflow-hidden rounded-2xl border border-slate-200 transition-shadow hover:shadow-lg"
    data-testid="seo-listing-card"
  >
    {d.image ? (
      <img src={d.image} alt={`${(d.type || "woning")} te huur in ${d.city}`} loading="lazy" className="h-40 w-full object-cover" />
    ) : (
      <div className="h-40 w-full bg-slate-100" />
    )}
    <div className="p-3">
      <div className="text-sm font-bold text-slate-900">
        {(d.type || "Woning")[0].toUpperCase() + (d.type || "woning").slice(1)} · {formatPrice(d.price)} p/m
      </div>
      <div className="text-xs text-slate-500">
        {d.m2} m² · {d.bedrooms} slaapkamers{d.neighborhood ? ` · ${d.neighborhood}` : ""}
      </div>
    </div>
  </Link>
);

const CategorySeo = ({ variant }) => {
  const params = useParams();
  const stad = params.stad;
  // Price variant: URL is /huurwoningen-onder-{bedrag}/{stad}; the first segment
  // arrives via :priceseg. Guard the exact shape to avoid catching stray paths.
  let maxPrice = null;
  let invalid = false;
  if (variant === "price") {
    const m = /^huurwoningen-onder-(\d+)$/.exec(params.priceseg || "");
    if (m) maxPrice = parseInt(m[1], 10);
    else invalid = true;
  }

  const [data, setData] = useState(null);

  useEffect(() => {
    if (invalid) return;
    setData(null);
    const kind = variant === "price" ? "huurwoningen" : KIND_BY_VARIANT[variant];
    const qs = maxPrice ? `?max_price=${maxPrice}` : "";
    api(`/seo/category/${kind}/${stad}${qs}`)
      .then(setData)
      .catch(() => setData({ found: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, stad, maxPrice, invalid]);

  useSeo(
    data && data.found !== false
      ? {
          title: data.page_title,
          exactTitle: true,
          description: data.meta_description,
          image: data.og_image,
          robots: data.robots,
          jsonLd: [data.jsonld, data.breadcrumb_jsonld].filter(Boolean),
        }
      : { title: "Huurwoningen", robots: "noindex,follow" }
  );

  if (invalid) return <Navigate to="/" replace />;

  if (!data) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-10">
        <div className="h-8 w-1/2 animate-pulse rounded bg-slate-100" />
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-52 animate-pulse rounded-2xl bg-slate-100" />)}
        </div>
      </div>
    );
  }

  const s = data.stats || { count: 0, avg_price: 0, avg_m2: 0, bands: {} };

  return (
    <div className="mx-auto max-w-5xl px-4 py-8" data-testid="seo-category-page">
      <nav className="mb-3 text-sm text-slate-400">
        <Link to={`/huurwoningen/${data.city_slug}`} className="hover:text-slate-600">Huurwoningen</Link>
        {" › "}<span>{data.city_name}</span>
        {data.type_label ? <>{" › "}<span>{data.type_label}</span></> : null}
      </nav>

      <h1 className="font-display text-3xl font-extrabold text-slate-900" data-testid="seo-h1">{data.h1}</h1>
      <p className="mt-1 text-slate-500">{data.intro}</p>

      <div className="mt-4">
        <PlayStoreButton placement="seo_city" />
      </div>

      <div className="mt-5 grid grid-cols-3 gap-3">
        <div className="rounded-2xl bg-slate-50 p-4"><div className="font-display text-2xl font-extrabold text-slate-900">{s.count}</div><div className="text-xs text-slate-500">Beschikbare woningen</div></div>
        <div className="rounded-2xl bg-slate-50 p-4"><div className="font-display text-2xl font-extrabold text-slate-900">{formatPrice(s.avg_price)}</div><div className="text-xs text-slate-500">Gem. huurprijs p/m</div></div>
        <div className="rounded-2xl bg-slate-50 p-4"><div className="font-display text-2xl font-extrabold text-slate-900">{s.avg_m2} m²</div><div className="text-xs text-slate-500">Gem. oppervlakte</div></div>
      </div>

      <p className="mt-4 leading-relaxed text-slate-600">{data.context_text}</p>

      {data.listings && data.listings.length > 0 ? (
        <>
          <h2 className="mt-8 font-display text-xl font-bold text-slate-900">Actuele huurwoningen in {data.city_name}</h2>
          <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3">
            {data.listings.map((d) => <Card key={d.slug} d={d} />)}
          </div>
        </>
      ) : (
        <div className="mt-6 rounded-2xl border border-orange-200 bg-orange-50 p-4 font-medium text-orange-800">
          Er zijn op dit moment geen woningen die aan deze zoekopdracht voldoen. Maak een gratis woningalert aan en ontvang direct een melding zodra er nieuw aanbod is.
        </div>
      )}

      {s.bands && Object.keys(s.bands).length > 0 && (
        <>
          <h3 className="mt-8 font-display text-lg font-bold text-slate-900">Prijsklassen in {data.city_name}</h3>
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {Object.entries(s.bands).map(([band, n]) => (
              <div key={band} className="rounded-2xl bg-slate-50 p-4"><div className="font-display text-xl font-extrabold text-slate-900">{n}</div><div className="text-xs text-slate-500">{band}</div></div>
            ))}
          </div>
        </>
      )}

      <div className="mt-8 rounded-3xl bg-navy p-7 text-white">
        <h2 className="font-display text-xl font-bold">Krijg een melding zodra een vergelijkbare woning online komt</h2>
        <p className="mt-2 text-slate-300">Woningen in {data.city_name} zijn vaak binnen enkele uren verhuurd. Met een gratis WoningAlert ben jij er als eerste bij — inclusief slimme Match Score en scam-check.</p>
        <Link to="/register" className="mt-4 inline-block rounded-full bg-brandblue px-6 py-3 font-bold text-white" data-testid="seo-cta-register">Maak gratis een woningalert aan</Link>
      </div>

      <h2 className="mt-8 font-display text-xl font-bold text-slate-900">Gerelateerde categorieën</h2>
      <div className="mt-3 flex flex-wrap gap-2" data-testid="seo-related">
        <Link to={`/huurwoningen/${data.city_slug}`} className="rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-700">Alle huurwoningen in {data.city_name}</Link>
        <Link to={`/appartement-huren/${data.city_slug}`} className="rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-700">Appartement huren in {data.city_name}</Link>
        <Link to={`/studio-huren/${data.city_slug}`} className="rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-700">Studio huren in {data.city_name}</Link>
        <Link to={`/kamer-huren/${data.city_slug}`} className="rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-700">Kamer huren in {data.city_name}</Link>
        <Link to={`/huurwoningen-onder-1500/${data.city_slug}`} className="rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-700">Huurwoningen onder €1.500 in {data.city_name}</Link>
      </div>

      {data.nearby && data.nearby.length > 0 && (
        <>
          <h3 className="mt-8 font-display text-lg font-bold text-slate-900">Huurwoningen in omliggende steden</h3>
          <div className="mt-3 flex flex-wrap gap-2">
            {data.nearby.map((nb) => (
              <Link key={nb.slug} to={`/huurwoningen/${nb.slug}`} className="rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-700">Huurwoningen in {nb.name}</Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default CategorySeo;
